import { computed, ref } from 'vue'
import { ParameterItem, widgetParameterApi } from '@/api/widgetParameterApi'
import { getEntityList } from '@/composables/globalData/useEntitiesData'
import { Category, Question } from '@/types/entities'
import i18n from '@/i18n'

export interface ParameterOption {
    label: string
    value: string
    tag: string
}

export interface ParameterGroup {
    label: string
    options: ParameterOption[]
}

/**
 * Composable for fetching and managing AI parameter data from 3 sources:
 * 1. AI Categories from Entities (Questions)
 * 2. CDR List from API
 * 3. Calls Custom Variables from API
 */
export function useAiParameterData () {
    const isLoading = ref(false)
    const isLoaded = ref(false)
    const error = ref<string | null>(null)

    // Raw data from sources
    const aiCategoriesData = ref<Category[]>([])
    const cdrListData = ref<ParameterItem[]>([])
    const customVariablesData = ref<ParameterItem[]>([])

    /**
     * Transforms AI Categories into ParameterGroup format
     * Maps Questions from each category with QuestionKey as value
     */
    const aiCategoriesGroup = computed<ParameterGroup>(() => {
        const questionsMap = new Map<string, ParameterOption>()

        // Collect all unique questions from all categories
        aiCategoriesData.value.forEach(category => {
            category.Questions?.forEach((question: Question) => {
                if (question.QuestionKey && !questionsMap.has(question.QuestionKey)) {
                    questionsMap.set(question.QuestionKey, {
                        label: question.QuestionKey,
                        value: question.QuestionKey,
                        tag: question.QuestionKey
                    })
                }
            })
        })

        return {
            label: String(i18n.global.t('widget.ai.groups.aiQuestions')),
            options: Array.from(questionsMap.values())
        }
    })

    /**
     * Transforms CDR List into ParameterGroup format
     */
    const cdrListGroup = computed<ParameterGroup>(() => ({
        label: String(i18n.global.t('widget.ai.groups.cdrFields')),
        options: cdrListData.value.map(item => ({
            label: item.label,
            value: item.value,
            tag: item.value
        }))
    }))

    /**
     * Transforms Custom Variables into ParameterGroup format
     */
    const customVariablesGroup = computed<ParameterGroup>(() => ({
        label: String(i18n.global.t('widget.ai.groups.customVariables')),
        options: customVariablesData.value.map(item => ({
            label: item.label,
            value: item.value,
            tag: item.value
        }))
    }))

    /**
     * Combined parameter groups from all sources
     * Only includes groups that have options
     */
    const parameterGroups = computed<ParameterGroup[]>(() => {
        const groups: ParameterGroup[] = []

        if (aiCategoriesGroup.value.options.length > 0) {
            groups.push(aiCategoriesGroup.value)
        }

        if (cdrListGroup.value.options.length > 0) {
            groups.push(cdrListGroup.value)
        }

        if (customVariablesGroup.value.options.length > 0) {
            groups.push(customVariablesGroup.value)
        }

        return groups
    })

    /**
     * Fetches data from all 3 sources in parallel
     */
    async function fetchAllData () {
        if (isLoading.value) return

        isLoading.value = true
        error.value = null

        try {
            // Fetch all data sources in parallel
            const [ cdrList, customVars ] = await Promise.all([
                widgetParameterApi.getCDRList(),
                widgetParameterApi.getCallsCustomVariablesList()
            ])

            // Get AI Categories from entity list (already cached)
            // Update reactive refs
            aiCategoriesData.value = getEntityList('AiCategories') || []
            cdrListData.value = cdrList
            customVariablesData.value = customVars

            isLoaded.value = true
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to fetch parameter data'
            console.error('Error fetching AI parameter data:', e)
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Refreshes data from all sources
     */
    async function refresh () {
        isLoaded.value = false
        await fetchAllData()
    }

    return {
        // State
        isLoading,
        isLoaded,
        error,

        // Raw data
        aiCategoriesData,
        cdrListData,
        customVariablesData,

        // Computed groups
        aiCategoriesGroup,
        cdrListGroup,
        customVariablesGroup,
        parameterGroups,

        // Methods
        fetchAllData,
        refresh
    }
}

/**
 * Creates and manages a singleton instance of the AI parameter data
 * Useful when the same data needs to be shared across components
 */
export function useSharedAiParameterData () {
    // Use module-level refs for singleton behavior
    const sharedInstance = useAiParameterData()

    // Auto-fetch on first use if not already loaded
    if (!sharedInstance.isLoaded.value && !sharedInstance.isLoading.value) {
        sharedInstance.fetchAllData()
    }

    return sharedInstance
}
